import React, { useEffect, useRef, useState } from "react";
import { Box, Grid, Typography, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import { formatAmount } from "./manageRequestsService";

interface OrderBypassData {
  amount: string;
  days: string;
  invoice: string;
}

interface Props {
  data?: OrderBypassData;
  onAutoSave?: (updated: OrderBypassData) => void;
  onDataChange?: (updateddata: OrderBypassData) => void
  status: string | undefined;
}

const OrderBypassRequest: React.FC<Props> = ({ data, onAutoSave, onDataChange, status }) => {
  const [editMode, setEditMode] = useState(false);
  const [fieldsVisible, setFieldsVisible] = useState(() => {
    if (!data) return false;
    return (
      (data.amount && data.amount !== "0") ||
      (data.days && data.days !== "0") ||
      (data.invoice && data.invoice !== "0")
    );
  });
  const [formData, setFormData] = useState<OrderBypassData>({
    amount: data?.amount || "0",
    days: data?.days || "0",
    invoice: data?.invoice || "0",
  });

  const containerRef = useRef<HTMLDivElement>(null);

  const boxStyle = {
    px: 2,
    bgcolor: "white",
    minHeight: "30px",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-start",
    justifyContent: "flex-start",
    pb: 2,
    fontSize: "12px",
    position: "relative" as const,
  };

  const fieldBox = {
    mt: 0.2,
    bgcolor: "white",
    px: 1.5,
    py: 0.5,
    borderRadius: 2,
    fontWeight: 500,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-start",
    minWidth: "150px",
    border: editMode ? "1px solid #bfbfc0" : "0.5px solid #bfbfc0",
    transition: "border-color 0.2s",
  };

  const handleChange = (field: keyof OrderBypassData, value: string) => {
    if (field === "amount") {
      if (value && !/^\d*\.?\d*$/.test(value)) {
        return;
      }
    } else if (field === "days" || field === "invoice") {
      if (value && !/^\d*$/.test(value)) {
        return;
      }
    }

    setFormData((prev) => {
      let updatedValue = value;

      if ((field === "amount" || field === "days" || field === "invoice") && prev[field] === "0" && value !== "0") {
        updatedValue = value.replace(/^0+/, "");
      }

      if(updatedValue == "") updatedValue = "0";
      const updated = { ...prev, [field]: updatedValue };
      onAutoSave?.(updated);
      onDataChange?.(updated);
      return updated;
    });
  };


  // Close edit mode on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        editMode &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setEditMode(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [editMode]);

  const handleEditClick = () => {
    if (!fieldsVisible) {
      setFieldsVisible(true); 
      setEditMode(true);
    } else {
      setEditMode((prev) => !prev);
    }
  };

  return (
    <Box sx={boxStyle} ref={containerRef}>
      {/* Edit Icon */}
      {status && status.toLowerCase() !== "rejected" && status.toLowerCase() !== "approved" && status.toLowerCase() !== "rfc_rejected" && status.toLowerCase() !== "rfc_approved" && (<IconButton
        onClick={handleEditClick}
        sx={{
          position: "absolute",
          // top: 5,
          right: 5,
          width: 26,
          height: 26,
          color: editMode ? "#2e7d32" : "#00897b",
          bgcolor: "rgba(0, 137, 123, 0.08)",
          borderRadius: "50%",
          transition: "all 0.2s ease",
          "& svg": { fontSize: 14 },
          "&:hover": {
            bgcolor: "rgba(0, 137, 123, 0.15)",
            transform: "scale(1.05)",
          },
          "&:active": {
            bgcolor: "rgba(0, 137, 123, 0.25)",
            transform: "scale(0.95)",
          },
        }}
      >
        {editMode ? <CheckIcon /> : <EditIcon />}
      </IconButton>)}

      {fieldsVisible && (
        <Grid container spacing={0.5} direction="column">
          <Grid item>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, pt: 1 }}>
              {[
                { label: "Amount", field: "amount", bg: "#FFF2F2" },
                { label: "Days", field: "days", bg: "#E9F2FF" },
                { label: "Invoice", field: "invoice", bg: "#EAF9E9" },
              ].map((item, i) => (
                <Box
                  key={i}
                  sx={{
                    ...fieldBox,
                    bgcolor: item.bg,
                    cursor: editMode ? "text" : "default",
                    border: editMode ? "1px solid #bfbfc0" : (item.bg == "white" ? "0.5px solid #bfbfc0" : "none")
                  }}
                >
                  <Typography
                    sx={{ fontSize: 12, fontWeight: 500, mr: 0.5 }}
                  >
                    {item.label}:
                  </Typography>

                  {editMode ? (
                    <input
                      type="text"
                      value={formData[item.field as keyof OrderBypassData]}
                      onChange={(e) =>
                        handleChange(
                          item.field as keyof OrderBypassData,
                          e.target.value
                        )
                      }
                      style={{
                        border: "none",
                        background: "transparent",
                        outline: "none",
                        width: "60px",
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "#333",
                      }}
                    />
                  ) : (
                    <Typography sx={{ fontSize: 12 }}>
                      {item.field === "amount" ? formatAmount(formData[item.field as keyof OrderBypassData] || "0") : formData[item.field as keyof OrderBypassData] || "0"}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default OrderBypassRequest;
